import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { HiOutlineCube, HiOutlineUsers, HiOutlineSparkles, HiOutlineClock, HiOutlineClipboardList, HiOutlineChat } from "react-icons/hi";
import { HiOutlinePencil, HiOutlineTrash, HiOutlinePlus, HiOutlineCheck, HiOutlineX } from "react-icons/hi";
import axios from "axios";
import { invalidarMermaTolerancia } from "../../lib/hooks/useMermaTolerancia";
import { invalidarCambioCaja, CAMBIO_CAJA_DEFECTO } from "../../lib/hooks/useCambioCaja";
import { UserContext } from "../../context/UserContext";
import { trialDaysLeft, shouldShowTrialBanner } from "../../lib/trial";
import { PLANES, planRecomendado, formatearEuros as formatMoneyEUR } from "../../lib/planes";
import { textoDuracion } from "../Elements/MembershipDateFields";
import useConfirmar from "../Elements/useConfirmar";
import Button from "../Elements/Button";
import ActaPlantillaCard from "./SettingsComponents/ActaPlantillaCard";
import PreinscripcionFormCard from "./SettingsComponents/PreinscripcionFormCard";
import "./Settings.css";

const BILLING_MODE_OPTIONS = [
  { value: "stripe", label: "Stripe normal" },
  { value: "partner_comped", label: "Partner comped" },
  { value: "manual_invoice", label: "Manual invoice" },
  { value: "paused", label: "Paused" },
];

const BILLING_MODE_LABELS = {
  stripe: "Stripe normal",
  partner_comped: "Partner comped",
  manual_invoice: "Manual invoice",
  paused: "Paused",
};

// Conversión temporal para mostrar el consumo de IA en euros mientras la API
// sigue devolviendo importes en USD.
const AI_USAGE_USD_TO_EUR_RATE = 1 / 1.1525;

function formatEuroAmount(value, minimumFractionDigits, maximumFractionDigits = minimumFractionDigits) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Number(value || 0));
}

// Plan Inversores (early stage): pago recurrente mensual vía Stripe. NO es un tier
// SaaS (no cambia límites de socios). La participación en la empresa se formaliza
// APARTE con un acuerdo tipo SAFE (ver TAREAS_PENDIENTES.md) — no por la suscripción.
// Mientras se invierte, los 350 €/mes SUSTITUYEN la cuota normal (se cancela la sub
// SaaS pero se mantiene el tier/límites). Al dejar de invertir se vuelve a la cuota
// normal (re-checkout manual). El checkout va por el backend, como los demás planes.
const INVESTOR_PLAN = {
  priceEUR: 350,
};

// El catálogo vive en `src/lib/planes.js`, espejo de `core/planes.py`. Antes estaba
// aquí en un `PLAN_META` suelto, y con él la promo del -50 % de lanzamiento: una
// tarjeta que prometía «-50 % 3 meses» sin NADA detrás que lo aplicara ni que lo
// revirtiera — el backend nunca supo de ella. Fuera (RF-008).
const PLAN_META = PLANES;

export default function Settings() {
  // El contexto es un ARRAY POSICIONAL con 18 elementos y 76 consumidores, así
  // que se lee por índice y no contando comas: contar comas es exactamente lo
  // que se rompe cuando alguien añade un valor. La posición está fijada por un
  // candado (ComanderoMenuSinRecargar.wave5.test.jsx).
  const userCtx = useContext(UserContext);
  const [token, , , venueID, , , , accessPrivileges] = userCtx;
  const aplicarOrdersEnabled = userCtx[17];
  // Posición 19 (candado ComanderoMenuSinRecargar): actualizador del flag de Smack.
  const aplicarSmackEnabled = userCtx[19];
  const [billingStatus, setBillingStatus] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [externalCardTerminal, setExternalCardTerminal] = useState(true);
  // Comandero (CRUO): solo superadmin lo activa (issue #456).
  const [ordersEnabled, setOrdersEnabled] = useState(false);
  // Smack (chat equipo+club): solo superadmin lo activa (mismo criterio).
  const [smackEnabled, setSmackEnabled] = useState(false);
  // Cuota de inscripción por defecto del local (editable por alta en el modal de socio).
  const [inscriptionFeeInput, setInscriptionFeeInput] = useState("");
  // Tara de frasco por defecto del local (los tarros suelen ser todos iguales).
  const [defaultTareInput, setDefaultTareInput] = useState("");
  // Tolerancia de báscula (merma máxima por línea, g). Vacío = default 0,10 g.
  const [mermaToleranciaInput, setMermaToleranciaInput] = useState("");
  // Cambio a dejar en la caja al cerrar el turno (€). Vacío = default 135 €.
  const [cambioCajaInput, setCambioCajaInput] = useState("");
  // Hora de corte de la jornada (0-23). 0 = día natural.
  const [dayCutoffInput, setDayCutoffInput] = useState("0");
  const [billingMode, setBillingMode] = useState("stripe");
  const [billingOverrideReason, setBillingOverrideReason] = useState("");
  const [billingOverrideExpiresAt, setBillingOverrideExpiresAt] = useState("");
  const [billingOverrideConfirm, setBillingOverrideConfirm] = useState("");
  // Trial de 30 días a nivel de app (SCRUM-53): fecha fin en formato YYYY-MM-DD.
  const [trialEndsAt, setTrialEndsAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [workingPlanId, setWorkingPlanId] = useState("");
  const [error, setError] = useState("");

  // ── Membership Durations ──────────────────────────────────────────────────
  const [durations, setDurations] = useState([]);
  const [durationsLoading, setDurationsLoading] = useState(false);
  const [editingDuration, setEditingDuration] = useState(null); // { id, label, months, days, price }
  const [newDuration, setNewDuration] = useState({ label: "", months: "", days: "", price: "" });
  const [showNewDurationForm, setShowNewDurationForm] = useState(false);
  const [durationsError, setDurationsError] = useState("");
  const { confirmar, dialogo: dialogoConfirmar } = useConfirmar();

  const currentPlanId = billingStatus?.plan_tier || "basico";
  const aiUsageCostEur = Number(aiUsage?.cost_usd || 0) * AI_USAGE_USD_TO_EUR_RATE;
  const aiUsageMonthlyCapEur = Number(aiUsage?.monthly_cap_usd || 0) * AI_USAGE_USD_TO_EUR_RATE;

  useEffect(() => {
    if (!token || !venueID) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");

      const authHeaders = { Authorization: `Bearer ${token}` };
      const loadErrors = [];

      try {
        const venueRes = await axios.get(`/v1/venues/${venueID}`, { headers: authHeaders });
        if (!cancelled) {
          const venueData = venueRes?.data || {};
          setExternalCardTerminal(Boolean(venueData.external_card_terminal ?? true));
          setOrdersEnabled(Boolean(venueData.orders_enabled));
          setSmackEnabled(Boolean(venueData.smack_enabled));
          setInscriptionFeeInput(
            venueData.inscription_fee != null ? String(Number(venueData.inscription_fee)) : ""
          );
          setDefaultTareInput(
            venueData.default_tare_grams != null ? String(Number(venueData.default_tare_grams)) : ""
          );
          setMermaToleranciaInput(
            venueData.merma_tolerancia_g != null ? String(Number(venueData.merma_tolerancia_g)) : ""
          );
          setCambioCajaInput(
            venueData.cambio_caja_defecto != null ? String(Number(venueData.cambio_caja_defecto)) : ""
          );
          setDayCutoffInput(String(Number(venueData.day_cutoff_hour ?? 0)));
          setBillingMode(venueData.billing_mode || "stripe");
          setBillingOverrideReason(venueData.billing_override_reason || "");
          setBillingOverrideExpiresAt(
            venueData.billing_override_expires_at
              ? String(venueData.billing_override_expires_at).slice(0, 10)
              : ""
          );
          setBillingOverrideConfirm("");
          setTrialEndsAt(
            venueData.trial_ends_at ? String(venueData.trial_ends_at).slice(0, 10) : ""
          );
        }
      } catch (err) {
        loadErrors.push(err?.response?.data?.detail || "No se pudo cargar la tienda.");
      }

      try {
        const billingRes = await axios.get("/v1/billing/status", {
          params: { venue_id: venueID },
          headers: authHeaders,
        });
        if (!cancelled) {
          setBillingStatus(billingRes.data);
          if (billingRes.data?.billing_mode) {
            setBillingMode(billingRes.data.billing_mode);
          }
        }
      } catch (err) {
        loadErrors.push(err?.response?.data?.detail || "No se pudo cargar el estado de facturación.");
      }

      try {
        const aiUsageRes = await axios.get("/v1/ai/usage", {
          params: { venue_id: venueID },
          headers: authHeaders,
        });
        if (!cancelled) {
          setAiUsage(aiUsageRes.data);
        }
      } catch (_err) {
        if (!cancelled) {
          setAiUsage(null);
        }
      }

      if (!cancelled && loadErrors.length > 0) {
        setError(loadErrors.join(" "));
      }

      if (!cancelled) {
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, venueID]);

  const plans = useMemo(() => {
    const currentPlanRank =
      PLAN_META.find((plan) => plan.id === currentPlanId)?.rank ??
      PLAN_META.find((plan) => plan.id === "basico")?.rank ??
      1;

    // Se pintan los planes vigentes, MÁS el del club si resulta ser uno de los
    // viejos: un club en `estandar` tiene que seguir viendo cuál es el suyo (RF-001).
    // Lo que no pasa es que se le ofrezcan los otros tres viejos a nadie.
    return PLAN_META.filter((plan) => plan.vigente || plan.id === currentPlanId).map(
      (plan) => ({
        ...plan,
        isCurrent: plan.id === currentPlanId,
        isUpgrade: plan.rank > currentPlanRank,
      })
    );
  }, [currentPlanId]);

  const isSuperadmin = accessPrivileges === "superadmin";
  // El acta es el documento legal del club: la cambia el dueño, no el staff.
  // El backend manda igual; esto solo evita enseñar un botón que va a dar 403.
  const puedeGestionarActa = isSuperadmin || accessPrivileges === "owner";
  const effectiveBillingMode = billingStatus?.billing_mode || billingMode;
  const stripeBillingOverridden = effectiveBillingMode !== "stripe";
  const requiresDangerConfirmation = billingMode !== "stripe";
  const canApplyPlanWithoutStripe = isSuperadmin && stripeBillingOverridden;

  const refreshBillingStatus = async () => {
    if (!token || !venueID) return;
    try {
      const billingRes = await axios.get("/v1/billing/status", {
        params: { venue_id: venueID },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBillingStatus(billingRes.data);
      if (billingRes.data?.billing_mode) {
        setBillingMode(billingRes.data.billing_mode);
      }
    } catch (_err) {
      // Keep current venue-derived billing mode if billing/status is unavailable.
    }
  };

  const handleApplyPlanDirectly = async (planId) => {
    if (!token || !venueID || !isSuperadmin) return;
    try {
      setWorkingPlanId(planId);
      setError("");
      const response = await axios.patch(
        `/v1/venues/${venueID}/plan-tier`,
        { plan_tier: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const venueData = response.data || {};
      setBillingStatus((prev) => (
        prev
          ? {
              ...prev,
              plan_tier: venueData.plan_tier || planId,
              max_socios: venueData.max_socios ?? prev.max_socios,
            }
          : {
              plan_tier: venueData.plan_tier || planId,
              max_socios: venueData.max_socios ?? null,
              socios_count: venueData.socios_count ?? 0,
              at_limit: false,
              billing_mode: effectiveBillingMode,
            }
      ));
      await refreshBillingStatus();
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo aplicar el plan sin Stripe.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleUpgrade = async (planId) => {
    if (!token || !venueID) return;
    // "Investor" is NOT a SaaS tier: it must never be applied directly (that would set
    // plan_tier="investor"). It always goes through Stripe checkout, and is blocked when
    // billing is overridden (the button is disabled for that case).
    if (canApplyPlanWithoutStripe && planId !== "investor") {
      await handleApplyPlanDirectly(planId);
      return;
    }
    try {
      setWorkingPlanId(planId);
      setError("");
      const response = await axios.post(
        "/v1/billing/checkout",
        { venue_id: venueID, plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.url) {
        window.location.href = response.data.url;
        return;
      }
      setError("Stripe no devolvió una URL de checkout.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo iniciar el checkout de Stripe.";
      if (err?.response?.status === 409 && isSuperadmin) {
        setError(
          `${detail} Guarda primero el override Partner comped abajo y vuelve a aplicar el plan.`
        );
      } else {
        setError(detail);
      }
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleManageBilling = async () => {
    if (!token || !venueID) return;
    try {
      setWorkingPlanId("portal");
      setError("");
      const response = await axios.post(
        "/v1/billing/portal",
        { venue_id: venueID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.url) {
        window.location.href = response.data.url;
        return;
      }
      setError("Stripe no devolvió una URL del portal.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "No se pudo abrir el portal de facturación.";
      setError(detail);
      if (detail.includes("No Stripe customer found")) {
        window.alert(
          "Todavía no existe una suscripción activa para este club. Primero debes iniciar el checkout de un plan para que Stripe cree el cliente y habilite la facturación."
        );
      }
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveBillingOverride = async () => {
    if (!token || !venueID || !isSuperadmin) return;
    if (billingMode !== "stripe" && !billingOverrideReason.trim()) {
      setError("Debes indicar un motivo para desactivar Stripe en esta tienda.");
      return;
    }
    if (requiresDangerConfirmation && billingOverrideConfirm.trim() !== "DISABLE STRIPE BILLING") {
      setError("Escribe exactamente DISABLE STRIPE BILLING para confirmar el override.");
      return;
    }

    try {
      setWorkingPlanId("billing-override");
      setError("");
      const response = await axios.patch(
        `/v1/venues/${venueID}/billing-override`,
        {
          billing_mode: billingMode,
          billing_override_reason: billingMode === "stripe" ? null : billingOverrideReason.trim(),
          billing_override_expires_at: billingMode === "stripe" || !billingOverrideExpiresAt ? null : `${billingOverrideExpiresAt}T00:00:00`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const venueData = response.data || {};
      setBillingMode(venueData.billing_mode || "stripe");
      setBillingOverrideReason(venueData.billing_override_reason || "");
      setBillingOverrideExpiresAt(
        venueData.billing_override_expires_at ? String(venueData.billing_override_expires_at).slice(0, 10) : ""
      );
      setBillingOverrideConfirm("");
      setBillingStatus((prev) => (
        prev
          ? {
              ...prev,
              billing_mode: venueData.billing_mode || "stripe",
              billing_override_reason: venueData.billing_override_reason || null,
              billing_override_expires_at: venueData.billing_override_expires_at || null,
            }
          : prev
      ));
      await refreshBillingStatus();
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar el override de facturación.");
    } finally {
      setWorkingPlanId("");
    }
  };

  // Trial (SCRUM-53): un solo endpoint para iniciar (now+30d), extender (fecha) y
  // terminar (null). El backend audita cada cambio en el Activity log.
  const handleSaveTrial = async (endsAtIso) => {
    if (!token || !venueID || !isSuperadmin) return;
    try {
      setWorkingPlanId("venue-trial");
      setError("");
      const response = await axios.patch(
        `/v1/venues/${venueID}/trial`,
        { trial_ends_at: endsAtIso },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const venueData = response.data || {};
      setTrialEndsAt(
        venueData.trial_ends_at ? String(venueData.trial_ends_at).slice(0, 10) : ""
      );
      await refreshBillingStatus();
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar el trial.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const fetchDurations = useCallback(async () => {
    if (!token || !venueID) return;
    setDurationsLoading(true);
    setDurationsError("");
    try {
      const res = await axios.get(`/v1/venues/${venueID}/membership-durations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDurations(res.data);
    } catch (err) {
      setDurationsError(err?.response?.data?.detail || "No se pudieron cargar las duraciones.");
    } finally {
      setDurationsLoading(false);
    }
  }, [token, venueID]);

  useEffect(() => { fetchDurations(); }, [fetchDurations]);

  const handleCreateDuration = async () => {
    // Vacío cuenta como 0: un preset puede ser solo meses, solo días o mixto,
    // pero nunca 0/0 (el backend lo rechaza igual, con la misma regla).
    const months = parseInt(newDuration.months, 10) || 0;
    const days = parseInt(newDuration.days, 10) || 0;
    if (months < 0 || days < 0) {
      setDurationsError("Meses y días no pueden ser negativos.");
      return;
    }
    if (!newDuration.label.trim() || months + days < 1) {
      setDurationsError("Introduce un nombre y una duración: al menos 1 mes o 1 día.");
      return;
    }
    setDurationsError("");
    try {
      await axios.post(
        `/v1/venues/${venueID}/membership-durations`,
        {
          label: newDuration.label.trim(),
          months,
          days,
          sort_order: durations.length,
          // Vacío = sin precio propio: la cuota usa el importe del local.
          price: newDuration.price === "" ? null : Number(newDuration.price),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewDuration({ label: "", months: "", days: "", price: "" });
      setShowNewDurationForm(false);
      await fetchDurations();
    } catch (err) {
      setDurationsError(err?.response?.data?.detail || "No se pudo crear la duración.");
    }
  };

  const handleUpdateDuration = async (id) => {
    const months = parseInt(editingDuration.months, 10) || 0;
    const days = parseInt(editingDuration.days, 10) || 0;
    if (months < 0 || days < 0) {
      setDurationsError("Meses y días no pueden ser negativos.");
      return;
    }
    if (!editingDuration.label.trim() || months + days < 1) {
      setDurationsError("Introduce un nombre y una duración: al menos 1 mes o 1 día.");
      return;
    }
    setDurationsError("");
    try {
      await axios.put(
        `/v1/venues/${venueID}/membership-durations/${id}`,
        {
          label: editingDuration.label.trim(),
          months,
          days,
          // null explícito = volver al precio del local (tri-estado del PUT;
          // fuera el sentinel -1 que cazó el code review).
          price: editingDuration.price === "" ? null : Number(editingDuration.price),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingDuration(null);
      await fetchDurations();
    } catch (err) {
      setDurationsError(err?.response?.data?.detail || "No se pudo actualizar la duración.");
    }
  };

  const handleDeleteDuration = async (id) => {
    // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
    // club y «Eliminar» moría sin decir nada (auditoría de UI, UI-003).
    const duracion = durations.find((d) => d.id === id);
    const eliminar = await confirmar({
      title: "Eliminar duración",
      description: duracion
        ? `Se eliminará «${duracion.label}». Los socios que ya la tienen no cambian.`
        : "Los socios que ya la tienen no cambian.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!eliminar) return;
    setDurationsError("");
    try {
      await axios.delete(`/v1/venues/${venueID}/membership-durations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDurations();
    } catch (err) {
      setDurationsError(err?.response?.data?.detail || "No se pudo eliminar la duración.");
    }
  };

  const handleSavePaymentSettings = async () => {
    if (!token || !venueID) return;
    try {
      setWorkingPlanId("payment-settings");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/payment-settings`,
        { external_card_terminal: externalCardTerminal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la configuración de TPV externo.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveOrdersEnabled = async () => {
    if (!token || !venueID) return;
    try {
      setWorkingPlanId("orders-enabled");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/orders-enabled`,
        { orders_enabled: ordersEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // El menú se pinta con el valor cacheado del contexto, así que sin esto
      // «Barra» no aparecía hasta la SEGUNDA recarga (reporte del usuario,
      // 18-ago). Ahora entra y sale en el acto.
      if (aplicarOrdersEnabled) aplicarOrdersEnabled(ordersEnabled);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la configuración del comandero.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveSmackEnabled = async () => {
    if (!token || !venueID) return;
    try {
      setWorkingPlanId("smack-enabled");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/smack-enabled`,
        { smack_enabled: smackEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Mismo motivo que el comandero: el menú se pinta con el valor cacheado
      // del contexto — sin esto «Smack» esperaría a la segunda recarga.
      if (aplicarSmackEnabled) aplicarSmackEnabled(smackEnabled);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la configuración de Smack.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveInscriptionFee = async () => {
    if (!token || !venueID) return;
    const fee = parseFloat(inscriptionFeeInput);
    if (Number.isNaN(fee) || fee < 0) {
      setError("La cuota de inscripción debe ser un número mayor o igual que 0.");
      return;
    }
    try {
      setWorkingPlanId("inscription-fee");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/inscription-fee`,
        { inscription_fee: fee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la cuota de inscripción.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveCambioCaja = async () => {
    if (!token || !venueID) return;
    // Campo vacío = volver al default del sistema — se manda null. 0 es válido.
    const bruto = cambioCajaInput.trim();
    const cambio = bruto === "" ? null : parseFloat(bruto.replace(",", "."));
    if (cambio !== null && (Number.isNaN(cambio) || cambio < 0)) {
      setError("El cambio de caja debe ser un número igual o mayor que 0 (o vacío para el default).");
      return;
    }
    try {
      setWorkingPlanId("cambio-caja");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/cambio-caja`,
        { cambio_caja_defecto: cambio },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // La pantalla de Cajas cachea el cambio por sesión: invalidar para que el
      // próximo montaje lea el nuevo.
      invalidarCambioCaja(venueID);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar el cambio de caja.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveMermaTolerancia = async () => {
    if (!token || !venueID) return;
    // Campo vacío = volver al default del sistema (0,10 g) — se manda null.
    const bruto = mermaToleranciaInput.trim();
    const tol = bruto === "" ? null : parseFloat(bruto.replace(",", "."));
    if (tol !== null && (Number.isNaN(tol) || tol <= 0)) {
      setError("La tolerancia de merma debe ser un número mayor que 0 (o vacío para el default).");
      return;
    }
    try {
      setWorkingPlanId("merma-tolerancia");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/merma-tolerancia`,
        { merma_tolerancia_g: tol },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Las pantallas operativas (dispensario, Sala, Barra) cachean la tolerancia
      // por sesión: invalidar para que el próximo montaje lea la nueva.
      invalidarMermaTolerancia(venueID);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la tolerancia de merma.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveDefaultTare = async () => {
    if (!token || !venueID) return;
    const tare = parseFloat(defaultTareInput);
    if (Number.isNaN(tare) || tare < 0) {
      setError("La tara debe ser un número mayor o igual que 0.");
      return;
    }
    try {
      setWorkingPlanId("default-tare");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/default-tare`,
        { default_tare_grams: tare },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la tara por defecto.");
    } finally {
      setWorkingPlanId("");
    }
  };

  const handleSaveDayCutoff = async () => {
    if (!token || !venueID) return;
    try {
      setWorkingPlanId("day-cutoff");
      setError("");
      await axios.patch(
        `/v1/venues/${venueID}/day-cutoff`,
        { day_cutoff_hour: Number(dayCutoffInput) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo guardar la hora de corte.");
    } finally {
      setWorkingPlanId("");
    }
  };

  return (
    <div className="wa-settings-content max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-4" style={{ fontFamily: "var(--wa-font-sans, inherit)" }}>
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Configuración</h1>
      <p className="text-gray-600 mb-4 sm:mb-8 text-sm sm:text-base">Planes y opciones de tu cuenta.</p>

      {/* Aviso de conversión del trial (SCRUM-53): solo cuando urge (≤7 días). El CTA
          son los planes de justo debajo. */}
      {shouldShowTrialBanner(billingStatus?.trial_ends_at) && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>
            Tu prueba termina en {trialDaysLeft(billingStatus?.trial_ends_at)}{" "}
            {trialDaysLeft(billingStatus?.trial_ends_at) === 1 ? "día" : "días"}.
          </strong>{" "}
          {/* RF-007: decir el plan que toca y lo que cuesta. Antes ponía «elige un
              plan abajo» y el club no sabía cuánto iba a pagar hasta que se bloqueaba. */}
          Tienes {Number(billingStatus?.socios_count ?? 0).toLocaleString("es-ES")}{" "}
          {Number(billingStatus?.socios_count ?? 0) === 1 ? "socio" : "socios"}: te
          corresponde el plan{" "}
          <strong>{planRecomendado(billingStatus?.socios_count).name}</strong> por{" "}
          {formatMoneyEUR(planRecomendado(billingStatus?.socios_count).price)}/mes.
        </div>
      )}

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineCube className="text-purple-600" />
          Planes
        </h2>

        {error && (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {billingStatus && (
          <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            Socios: {billingStatus.socios_count} / {billingStatus.max_socios ?? "∞"}
            {billingStatus.at_limit ? " · Límite alcanzado" : ""}
            {billingStatus.billing_mode && billingStatus.billing_mode !== "stripe"
              ? ` · Override: ${BILLING_MODE_LABELS[billingStatus.billing_mode] || billingStatus.billing_mode}`
              : ""}
          </div>
        )}
        {aiUsage && (
          <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            IA este mes: {formatEuroAmount(aiUsageCostEur, 3)} / {formatEuroAmount(aiUsageMonthlyCapEur, 2)}
            {" · "}
            {Number(aiUsage.usage_percent || 0).toFixed(2)}%
            {" · "}
            {aiUsage.requests_count || 0} solicitudes
            {" · "}
            {aiUsage.total_tokens || 0} tokens
          </div>
        )}

        <div className="wa-settings-planes-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {plans.map((plan) => (
            (() => {
              return (
            <div
              key={plan.id}
              className={`relative rounded-xl border-2 bg-white p-4 sm:p-5 shadow-sm flex flex-col ${
                plan.isCurrent
                  ? "border-purple-500 bg-purple-50/30 ring-2 ring-purple-200"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              {plan.isCurrent && (
                <span className="absolute top-0 right-0 rounded-bl-lg bg-purple-600 text-white text-xs font-medium px-2 py-1">
                  Plan actual
                </span>
              )}
              <div className="font-semibold text-gray-900 text-lg">{plan.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-gray-900">{formatMoneyEUR(plan.price)}</span>
                <span className="text-sm text-gray-500">/mes</span>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-gray-600 flex-1">
                <li className="flex items-center gap-2">
                  <HiOutlineUsers className="text-purple-600 shrink-0" />
                  {plan.sociosLabel}
                </li>
                <li className="flex items-center gap-2">
                  <HiOutlineCube className="text-purple-600 shrink-0" />
                  {/* Los planes nuevos no llevan cuota de alta (RF-006); los viejos sí. */}
                  {Number(plan.setupFee) > 0
                    ? `Alta inicial: ${formatMoneyEUR(plan.setupFee)}`
                    : "Sin cuota de alta"}
                </li>
                <li className="flex items-center gap-2">
                  <HiOutlineSparkles className="text-purple-600 shrink-0" />
                  {plan.iaPromptsLabel}
                </li>
              </ul>
              {plan.isUpgrade && (
                <button
                  type="button"
                  disabled={loading || !!workingPlanId || (stripeBillingOverridden && !canApplyPlanWithoutStripe)}
                  onClick={() => handleUpgrade(plan.id)}
                  className="mt-5 w-full py-2.5 px-4 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {workingPlanId === plan.id
                    ? (canApplyPlanWithoutStripe ? "Aplicando..." : "Redirigiendo...")
                    : (canApplyPlanWithoutStripe ? `Aplicar ${plan.name}` : plan.upgradeLabel)}
                </button>
              )}
              {plan.isCurrent && (
                <button
                  type="button"
                  disabled
                  className="mt-5 w-full py-2.5 px-4 rounded-lg font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                >
                  Tu plan actual
                </button>
              )}
            </div>
              );
            })()
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          * Uso IA sujeto a política de uso razonable por plan para mantener calidad y tiempos de respuesta.
        </p>

        {/* Plan Inversores (early stage) — a lo ancho, debajo de los planes (no como columna) */}
        <div className="mt-6 rounded-xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-white p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <HiOutlineSparkles className="text-purple-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-purple-700">Early stage</span>
              </div>
              <h3 className="mt-1 text-lg font-semibold text-gray-900">Invierte en ClubsAgent</h3>
              <p className="mt-1 text-sm text-gray-600 max-w-2xl">
                Aportación mensual de <span className="font-semibold text-gray-900">350 €/mes</span> como
                inversor en fase inicial. La participación en la empresa se formaliza aparte mediante un
                acuerdo (SAFE). Puedes <span className="font-medium">dejar de invertir cuando quieras</span> y
                volver a tu cuota normal.
              </p>
            </div>
            <div className="flex items-baseline gap-1 sm:flex-col sm:items-end shrink-0">
              <span className="text-3xl font-bold text-gray-900">350 €</span>
              <span className="text-sm text-gray-500">/mes</span>
            </div>
          </div>
          {billingStatus?.investor_active ? (
            <div className="mt-5 w-full py-3 px-5 rounded-lg font-semibold text-center bg-green-100 text-green-800 border border-green-300 text-base">
              ✓ Eres inversor — {INVESTOR_PLAN.priceEUR} €/mes activo
              <span className="block text-xs font-normal text-green-700 mt-1">
                Puedes dejar de invertir cuando quieras desde “Gestionar facturación”.
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleUpgrade("investor")}
              disabled={loading || !!workingPlanId}
              className="mt-5 w-full py-3 px-5 rounded-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
            >
              {workingPlanId === "investor"
                ? "Redirigiendo a Stripe..."
                : `Convertirse en inversor — ${INVESTOR_PLAN.priceEUR} €/mes`}
            </button>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            disabled={loading || !!workingPlanId || stripeBillingOverridden}
            onClick={handleManageBilling}
            className="py-2.5 px-5 rounded-lg font-medium text-purple-700 border border-purple-300 bg-white hover:bg-purple-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {workingPlanId === "portal" ? "Abriendo portal..." : "Gestionar facturación"}
          </button>
        </div>
        {stripeBillingOverridden && (
          <p className="mt-3 text-sm text-amber-700">
            Stripe está desactivado para este club por override de superadmin.
            {canApplyPlanWithoutStripe
              ? " Puedes aplicar un plan directamente con los botones de arriba (sin checkout de Stripe)."
              : " El portal y nuevos checkouts quedan bloqueados."}
          </p>
        )}
      </section>

      {isSuperadmin && (
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <HiOutlineCube className="text-red-600" />
            Billing Override
          </h2>
          <div className="rounded-xl border border-red-300 bg-red-50 p-5">
            <div className="rounded-lg border border-red-200 bg-white/70 p-4 text-sm text-red-800">
              Esto desactiva la facturación automática de Stripe para esta tienda. No reutiliza el ajuste de TPV y afecta checkout, portal y control de cobro SaaS.
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="text-sm text-gray-700">
                <span className="block mb-1 font-medium">Modo de facturación</span>
                <select
                  value={billingMode}
                  onChange={(e) => setBillingMode(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
                >
                  {BILLING_MODE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                <span className="block mb-1 font-medium">Fecha de revisión (aviso — no corta el servicio)</span>
                <input
                  type="date"
                  value={billingOverrideExpiresAt}
                  onChange={(e) => setBillingOverrideExpiresAt(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
                />
              </label>
            </div>
            {billingStatus?.billing_override_expired && (
              <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">
                ⚠️ <span className="font-semibold">Override vencido</span>
                {billingStatus?.billing_override_expires_at
                  ? ` el ${new Date(billingStatus.billing_override_expires_at).toLocaleDateString("es-ES")}`
                  : ""} — sigue activo (la fecha solo avisa, nunca bloquea). Revísalo: renueva la fecha o devuelve el club a Stripe.
              </div>
            )}
            <label className="mt-4 block text-sm text-gray-700">
              <span className="block mb-1 font-medium">Motivo del override</span>
              <textarea
                value={billingOverrideReason}
                onChange={(e) => setBillingOverrideReason(e.target.value)}
                rows={3}
                placeholder="Ej: Partner estratégico con facturación exenta hasta nuevo acuerdo."
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2"
              />
            </label>
            {/* Conflicto inversor + override: el override bloquea el portal ("Gestionar
                facturación") y nuevos checkouts, así que un club inversor no podría dejar de
                invertir desde la UI. La suscripción de inversor en Stripe NO se cancela. */}
            {billingStatus?.investor_active && billingMode !== "stripe" && (
              <div className="mt-4 rounded-lg border border-amber-400 bg-amber-50 p-4 text-sm text-amber-900">
                ⚠️ <strong>Este club es INVERSOR activo</strong> (350&nbsp;€/mes vía Stripe). Activar un
                override le bloquea «Gestionar facturación» y nuevos checkouts; la suscripción de
                inversor <strong>seguirá cobrándose</strong> en Stripe. Asegúrate de que es intencionado.
              </div>
            )}
            {requiresDangerConfirmation && (
              <label className="mt-4 block text-sm text-gray-700">
                <span className="block mb-1 font-medium">Confirmación obligatoria</span>
                <input
                  type="text"
                  value={billingOverrideConfirm}
                  onChange={(e) => setBillingOverrideConfirm(e.target.value)}
                  placeholder="DISABLE STRIPE BILLING"
                  className="w-full rounded-lg border border-red-300 bg-white px-3 py-2"
                />
              </label>
            )}
            <div className="mt-4 flex items-center justify-between gap-3">
              {/* Estado GUARDADO en el servidor (verificación real), no la selección del dropdown.
                  Antes mostraba `billingMode` (estado local): al tocar el select parecía que el
                  override ya estaba aplicado sin haber guardado. */}
              <div className="text-sm text-gray-600">
                Estado actual (guardado):{" "}
                <strong>{BILLING_MODE_LABELS[effectiveBillingMode] || effectiveBillingMode}</strong>
                {billingMode !== effectiveBillingMode && (
                  <span className="ml-2 text-amber-700">
                    — selección sin guardar: {BILLING_MODE_LABELS[billingMode] || billingMode}
                  </span>
                )}
              </div>
              <button
                type="button"
                disabled={loading || !!workingPlanId}
                onClick={handleSaveBillingOverride}
                className="py-2.5 px-5 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {workingPlanId === "billing-override" ? "Guardando..." : "Guardar billing override"}
              </button>
            </div>
          </div>
        </section>
      )}

      {isSuperadmin && (
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <HiOutlineClock className="text-emerald-600" />
            Trial de prueba (30 días)
          </h2>
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-5">
            <div className="rounded-lg border border-emerald-200 bg-white/70 p-4 text-sm text-emerald-900">
              Prueba a nivel de app, sin tarjeta: mientras la fecha esté en el futuro, la tienda
              opera como activa sin pasar por Stripe. Al vencer, vuelve a quedar bloqueada si no
              ha contratado un plan. Cada cambio queda auditado en el registro de actividad.
            </div>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <button
                type="button"
                disabled={loading || !!workingPlanId}
                onClick={() => handleSaveTrial(new Date(Date.now() + 30 * 86400000).toISOString())}
                className="py-2.5 px-5 rounded-lg font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {workingPlanId === "venue-trial" ? "Guardando..." : "Iniciar trial (30 días)"}
              </button>
              <label className="text-sm text-gray-700">
                <span className="block mb-1 font-medium">Fin del trial (editar para extender)</span>
                <input
                  type="date"
                  value={trialEndsAt}
                  onChange={(e) => setTrialEndsAt(e.target.value)}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2"
                />
              </label>
              <button
                type="button"
                disabled={loading || !!workingPlanId || !trialEndsAt}
                onClick={() => handleSaveTrial(`${trialEndsAt}T23:59:59Z`)}
                className="py-2.5 px-4 rounded-lg font-medium text-emerald-700 border border-emerald-400 bg-white hover:bg-emerald-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Guardar fecha
              </button>
              <button
                type="button"
                disabled={loading || !!workingPlanId || !billingStatus?.trial_ends_at}
                onClick={() => handleSaveTrial(null)}
                className="py-2.5 px-4 rounded-lg font-medium text-red-700 border border-red-300 bg-white hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Terminar trial
              </button>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Estado actual (guardado):{" "}
              <strong>
                {billingStatus?.trial_ends_at
                  ? `trial hasta ${String(billingStatus.trial_ends_at).slice(0, 10)}`
                  : "sin trial"}
              </strong>
            </p>
          </div>
        </section>
      )}

      {/* ── Membership Durations ─────────────────────────────────────────────── */}
      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-1">
          <HiOutlineClock className="text-lime-600" />
          Duración de Inscripción
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Botones de duración que aparecen al crear un socio. Al pulsar uno, la inscripción se fija en la fecha de hoy y la caducidad se calcula automáticamente.
        </p>

        {durationsError && (
          <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {durationsError}
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-2">
          {durationsLoading ? (
            <p className="text-sm text-gray-400">Cargando…</p>
          ) : (
            <>
              {durations.map((d) => (
                <div key={d.id} className="wa-settings-duration-row flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  {editingDuration?.id === d.id ? (
                    <>
                      <input
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                        value={editingDuration.label}
                        onChange={(e) => setEditingDuration((p) => ({ ...p, label: e.target.value }))}
                        placeholder="Nombre (ej: 6 Meses)"
                      />
                      <input
                        type="number"
                        min="0"
                        className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                        value={editingDuration.months}
                        onChange={(e) => setEditingDuration((p) => ({ ...p, months: e.target.value }))}
                        placeholder="Meses"
                      />
                      <input
                        type="number"
                        min="0"
                        className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                        value={editingDuration.days}
                        onChange={(e) => setEditingDuration((p) => ({ ...p, days: e.target.value }))}
                        placeholder="Días"
                        title="Días sueltos que se suman a los meses. Solo días también vale (ej: pase de 1 día)."
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                        value={editingDuration.price}
                        onChange={(e) => setEditingDuration((p) => ({ ...p, price: e.target.value }))}
                        placeholder="€ (opcional)"
                        title="Precio de la cuota para este periodo. Vacío = el del local."
                      />
                      {(() => {
                        /* La cuenta que nadie hacía. Un club que cobra por meses
                           piensa «el año son 12 × 20 = 240, y se lo dejo en 200».
                           Este botón rellena los 240 para que solo haya que
                           escribir el precio final; el descuento queda dentro del
                           precio, que es donde lo quiere el club (CRUO, 25-ago).
                           No cambia nada solo: hay que pulsarlo. */
                        const mes = Number(inscriptionFeeInput);
                        const meses = Number(editingDuration.months);
                        if (!Number.isFinite(mes) || mes <= 0 || !Number.isFinite(meses) || meses <= 1) return null;
                        const total = (mes * meses).toFixed(2);
                        if (String(editingDuration.price) === total) return null;
                        return (
                          <button
                            type="button"
                            onClick={() => setEditingDuration((p) => ({ ...p, price: total }))}
                            className="shrink-0 rounded-lg border border-lime-300 bg-lime-50 px-2 py-1 text-[11px] font-medium text-lime-800 hover:bg-lime-100"
                            title={`${meses} meses a ${mes.toFixed(2)} € son ${total} €. Rellena ese importe para que lo ajustes a la baja.`}
                          >
                            = {total} €
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={() => handleUpdateDuration(d.id)}
                        className="p-1.5 rounded-lg bg-lime-500 text-white hover:bg-lime-600"
                        title="Guardar"
                      >
                        <HiOutlineCheck className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingDuration(null)}
                        className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100"
                        title="Cancelar"
                      >
                        <HiOutlineX className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 text-sm font-medium text-gray-800">{d.label}</span>
                      <span className="text-xs text-gray-400 w-28 shrink-0">{textoDuracion(d)}</span>
                      {(() => {
                        /* El precio de la duración NO se multiplica por los meses:
                           si está vacío se cobra la cuota del club tal cual. Así
                           que un «1 Año» sin precio cobra un mes — 20 € por un
                           año— y nada lo avisaba. Aquí se avisa, y cuando sí hay
                           precio se dice cuánto ahorra el socio frente a pagar
                           mes a mes, que es como lo piensan los clubes. */
                        const mes = Number(inscriptionFeeInput);
                        const hayMes = Number.isFinite(mes) && mes > 0;
                        /* Los días cuentan a razón de mes de 30 — SOLO para esta
                           pista. Sin esto, «1 mes y 15 días» sin precio escapaba
                           del aviso (months no llega a 2) y reabría la trampa
                           del «1 Año que cobra un mes» para los presets nuevos. */
                        const mesesEquivalentes = (Number(d.months) || 0) + (Number(d.days) || 0) / 30;
                        const sueltos = hayMes ? mes * mesesEquivalentes : null;
                        if (d.price != null) {
                          const ahorro = sueltos != null ? sueltos - Number(d.price) : null;
                          return (
                            <span className="w-36 shrink-0 text-right text-xs tabular-nums text-gray-700">
                              <span className="font-medium">{Number(d.price).toFixed(2)} €</span>
                              {ahorro != null && ahorro > 0 && (
                                <span className="block text-[10px] font-medium text-emerald-700">
                                  ahorra {ahorro.toFixed(2)} €
                                </span>
                              )}
                            </span>
                          );
                        }
                        return (
                          <span className="w-36 shrink-0 text-right text-xs tabular-nums">
                            <span className="text-gray-400">
                              {hayMes ? `${mes.toFixed(2)} € · del club` : "Sin precio"}
                            </span>
                            {mesesEquivalentes > 1 && hayMes && (
                              <span
                                className="block text-[10px] font-semibold text-amber-700"
                                title={`Se cobrará ${mes.toFixed(2)} €, no ${sueltos.toFixed(2)} €. El precio de la duración no se multiplica por los meses.`}
                              >
                                ⚠ cobra {mes.toFixed(2)} €, no {sueltos.toFixed(2)} €
                              </span>
                            )}
                          </span>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={() => setEditingDuration({ id: d.id, label: d.label, months: d.months ?? 0, days: d.days ?? 0, price: d.price ?? "" })}
                        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        title="Editar"
                      >
                        <HiOutlinePencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteDuration(d.id)}
                        className="p-1.5 rounded-lg border border-gray-200 text-red-400 hover:bg-red-50 hover:text-red-600"
                        title="Eliminar"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {showNewDurationForm ? (
                <div className="flex items-center gap-3 pt-2">
                  <input
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                    placeholder="Nombre (ej: 6 Meses)"
                    value={newDuration.label}
                    onChange={(e) => setNewDuration((p) => ({ ...p, label: e.target.value }))}
                  />
                  <input
                    type="number"
                    min="0"
                    className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                    placeholder="Meses"
                    value={newDuration.months}
                    onChange={(e) => setNewDuration((p) => ({ ...p, months: e.target.value }))}
                  />
                  <input
                    type="number"
                    min="0"
                    className="w-20 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                    placeholder="Días"
                    title="Días sueltos que se suman a los meses. Solo días también vale (ej: pase de 1 día)."
                    value={newDuration.days}
                    onChange={(e) => setNewDuration((p) => ({ ...p, days: e.target.value }))}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-lime-400 focus:ring-1 focus:ring-lime-400 outline-none"
                    placeholder="€ (opcional)"
                    title="Precio de la cuota para este periodo. Vacío = el del local."
                    value={newDuration.price}
                    onChange={(e) => setNewDuration((p) => ({ ...p, price: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={handleCreateDuration}
                    className="p-1.5 rounded-lg bg-lime-500 text-white hover:bg-lime-600"
                    title="Crear"
                  >
                    <HiOutlineCheck className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowNewDurationForm(false); setNewDuration({ label: "", months: "", days: "", price: "" }); }}
                    className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100"
                    title="Cancelar"
                  >
                    <HiOutlineX className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNewDurationForm(true)}
                  className="mt-2 flex items-center gap-1.5 text-sm font-medium text-lime-600 hover:text-lime-700"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  Añadir duración
                </button>
              )}
            </>
          )}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineCube className="text-purple-600" />
          Pagos en Tienda
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={externalCardTerminal}
              onChange={(e) => setExternalCardTerminal(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-700">
              Usar <strong>TPV externo</strong> para pagos con tarjeta.
              <span className="block text-gray-500 mt-1">
                Cuando está activo, ClubsAgent no cobra la tarjeta: solo pide confirmación y referencia del cobro en TPV.
              </span>
            </span>
          </label>
          <div className="mt-4">
            <button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSavePaymentSettings}
              className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {workingPlanId === "payment-settings" ? "Guardando..." : "Guardar configuración de pagos"}
            </button>
          </div>
        </div>
      </section>

      {/* Comandero (CRUO): habilitarlo es una decisión de onboarding, no un ajuste
          que el club pueda darse a sí mismo — de ahí el solo-superadmin, que el API
          también exige (PATCH /orders-enabled). */}
      {isSuperadmin && (
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <HiOutlineClipboardList className="text-purple-600" />
            Comandero
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={ordersEnabled}
                onChange={(e) => setOrdersEnabled(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                Habilitar el <strong>comandero</strong> en este club.
                <span className="block text-gray-500 mt-1">
                  Activa la pantalla de Barra y permite enviar comandas. Los clubs nacen sin él.
                </span>
              </span>
            </label>
            <div className="mt-4">
              <button
                type="button"
                disabled={loading || !!workingPlanId}
                onClick={handleSaveOrdersEnabled}
                className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {workingPlanId === "orders-enabled" ? "Guardando..." : "Guardar comandero"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Smack: chat equipo↔club. Igual que el comandero, habilitarlo es decisión
          de onboarding (solo superadmin; el API exige lo mismo en su PATCH). Al
          activarlo, el backend auto-crea el canal "general" del club. */}
      {isSuperadmin && (
        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <HiOutlineChat className="text-purple-600" />
            Smack
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={smackEnabled}
                onChange={(e) => setSmackEnabled(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                Habilitar <strong>Smack</strong> (chat con el equipo) en este club.
                <span className="block text-gray-500 mt-1">
                  Activa la sección de canales y el botón de reportar. Los clubs nacen sin él.
                </span>
              </span>
            </label>
            <div className="mt-4">
              <button
                type="button"
                disabled={loading || !!workingPlanId}
                onClick={handleSaveSmackEnabled}
                className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {workingPlanId === "smack-enabled" ? "Guardando..." : "Guardar Smack"}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineUsers className="text-purple-600" />
          Cuota de inscripción
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            Importe que se propone por defecto al dar de alta un socio nuevo. En cada alta
            se puede ajustar o desmarcar; pon <strong>0</strong> si tu club no cobra inscripción.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={inscriptionFeeInput}
                onChange={(e) => setInscriptionFeeInput(e.target.value)}
                className="w-32 rounded-lg border border-gray-300 px-3 py-2 pr-7 text-sm focus:border-purple-500 focus:ring-purple-500"
                aria-label="Cuota de inscripción por defecto"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
            </div>
            <button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSaveInscriptionFee}
              className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {workingPlanId === "inscription-fee" ? "Guardando..." : "Guardar cuota"}
            </button>
          </div>
        </div>
      </section>

      <ActaPlantillaCard
        token={token}
        venueID={venueID}
        puedeGestionar={puedeGestionarActa}
        onError={setError}
      />

      <PreinscripcionFormCard
        token={token}
        venueID={venueID}
        puedeGestionar={puedeGestionarActa}
        onError={setError}
      />

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineCube className="text-purple-600" />
          Tara de frasco por defecto
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            Peso del tarro vacío que se usa al pesar con frasco cuando el producto o la
            ubicación no tienen tara propia. Si todos tus tarros pesan lo mismo, ponla
            aquí una vez y vale para todos los productos.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.001"
                value={defaultTareInput}
                onChange={(e) => setDefaultTareInput(e.target.value)}
                className="w-32 rounded-lg border border-gray-300 px-3 py-2 pr-7 text-sm focus:border-purple-500 focus:ring-purple-500"
                aria-label="Tara de frasco por defecto"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">g</span>
            </div>
            <button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSaveDefaultTare}
              className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {workingPlanId === "default-tare" ? "Guardando..." : "Guardar tara"}
            </button>
          </div>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineCube className="text-slate-600" />
          Cambio a dejar en la caja
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            Cantidad de cambio que se propone dejar en la caja al cerrar el turno; pasa a
            ser el saldo de apertura del siguiente. En cada cierre se puede cambiar. Vacío =
            usar el estándar del sistema ({CAMBIO_CAJA_DEFECTO} €).
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={cambioCajaInput}
                onChange={(e) => setCambioCajaInput(e.target.value)}
                placeholder={String(CAMBIO_CAJA_DEFECTO)}
                className="w-32 rounded-lg border border-gray-300 px-3 py-2 pr-7 text-sm focus:border-slate-500 focus:ring-slate-500"
                aria-label="Cambio a dejar en la caja"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
            </div>
            <Button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSaveCambioCaja}
            >
              {workingPlanId === "cambio-caja" ? "Guardando..." : "Guardar cambio"}
            </Button>
          </div>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineCube className="text-purple-600" />
          Tolerancia de báscula (merma)
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            Diferencia máxima permitida entre los gramos cobrados y el peso real de
            báscula, por línea (merma). Se aplica en el dispensario, en la Sala y al
            servir comandas en la Barra. Vacío = usar el estándar del sistema
            (±0,10 g).
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={mermaToleranciaInput}
                onChange={(e) => setMermaToleranciaInput(e.target.value)}
                placeholder="0.10"
                className="w-32 rounded-lg border border-gray-300 px-3 py-2 pr-7 text-sm focus:border-purple-500 focus:ring-purple-500"
                aria-label="Tolerancia de merma"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">g</span>
            </div>
            <button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSaveMermaTolerancia}
              className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {workingPlanId === "merma-tolerancia" ? "Guardando..." : "Guardar tolerancia"}
            </button>
          </div>
        </div>
      </section>

      <section className="mb-10">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <HiOutlineClock className="text-purple-600" />
          Jornada del club
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-700">
            Hora a la que <strong>termina</strong> la jornada. Si cerráis de madrugada
            (p. ej. abrís a las 11:00 y cerráis a las 02:00), pon las 06:00: todo lo
            anterior a esa hora contará en las estadísticas como jornada del día
            anterior. Déjalo en 00:00 si abrís y cerráis el mismo día.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <select
              value={dayCutoffInput}
              onChange={(e) => setDayCutoffInput(e.target.value)}
              className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
              aria-label="Hora de corte de la jornada"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</option>
              ))}
            </select>
            <button
              type="button"
              disabled={loading || !!workingPlanId}
              onClick={handleSaveDayCutoff}
              className="py-2.5 px-5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {workingPlanId === "day-cutoff" ? "Guardando..." : "Guardar jornada"}
            </button>
          </div>
        </div>
      </section>
      {dialogoConfirmar}
    </div>
  );
}
